import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetApiInvitationsToken,
  usePostApiInvitationsTokenAccept,
  useGetApiWorkspaces,
} from '@/api/generated/workspaces/workspaces';
import { useAuthStore } from '@/stores/useAuthStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LogoIcon } from '@/components/common/Logo';
import {
  Building2,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Loader2,
  LogIn,
  UserPlus,
  LogOut,
  Info,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

export const AcceptInvitePage: React.FC = () => {
  const { token: pathToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token') || '';
  const queryEmail = searchParams.get('email');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isAuthenticated, user, logout } = useAuthStore();
  const { workspaces, setWorkspaces, setWorkspace } = useWorkspaceStore();
  const { refetch: refetchWorkspaces } = useGetApiWorkspaces();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch invitation details and validity
  const {
    data: invitationData,
    isLoading: isLoadingInvite,
    isError: isInviteError,
  } = useGetApiInvitationsToken(token, {
    query: {
      enabled: !!token,
      retry: false,
    },
  });

  const acceptMutation = usePostApiInvitationsTokenAccept();

  const inviteDetails = invitationData as any;
  const targetEmail = inviteDetails?.email || queryEmail || '';
  const workspaceName = inviteDetails?.workspaceName || 'Workspace';
  const roleName = inviteDetails?.role || 'Membro';
  const isValid = inviteDetails?.isValid !== false && !inviteDetails?.isExpired;

  const isEmailMismatch = Boolean(
    isAuthenticated &&
    user?.email &&
    targetEmail &&
    user.email.toLowerCase() !== targetEmail.toLowerCase()
  );

  const isAlreadyMember = Boolean(
    isAuthenticated &&
    inviteDetails?.workspaceId &&
    workspaces.some((w) => w.id === inviteDetails.workspaceId)
  );

  const inviteReturnUrl = encodeURIComponent(`/invitations/${token}`);
  const encodedEmail = encodeURIComponent(targetEmail);

  const isUserRegistered = inviteDetails?.isUserRegistered ?? false;

  // Handle account logout and transition to login for target email
  const handleSwitchAccount = () => {
    logout();
    queryClient.clear();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('chatagent_auth');
      localStorage.removeItem('chatagent_workspace');
    }
    const targetRoute = isUserRegistered ? '/login' : '/register';
    navigate(`${targetRoute}?returnUrl=${inviteReturnUrl}&email=${encodedEmail}`);
  };

  // Handle invitation acceptance
  const onAcceptInvite = async () => {
    if (!token) return;
    setErrorMessage(null);

    try {
      const res = (await acceptMutation.mutateAsync({
        token,
      })) as any;

      toast.success('Convite aceito com sucesso! Bem-vindo ao time.');

      // Refresh workspaces list and set newly joined workspace active
      try {
        const { data: workspacesList } = await refetchWorkspaces();
        if (workspacesList) {
          setWorkspaces(workspacesList as any[]);
          const wsId = res?.workspaceId || inviteDetails?.workspaceId;
          if (wsId) {
            setWorkspace(wsId);
          }
        }
      } catch {
        // ignore refetch error
      }

      navigate('/conversations', { replace: true });
    } catch (err: any) {
      const errorCode = err?.response?.data?.error || err?.response?.data?.code;
      let msg =
        err?.response?.data?.description ||
        err?.response?.data?.message ||
        'Não foi possível aceitar o convite. Verifique se o convite ainda é válido.';

      if (errorCode === 'Workspace.InvitationEmailMismatch' || errorCode === 'InvitationEmailMismatch') {
        msg = `Este convite foi enviado para ${targetEmail}. Você está conectado como ${user?.email}.`;
      } else if (errorCode === 'Workspace.UserAlreadyMember' || errorCode === 'UserAlreadyMember') {
        msg = 'Você já é membro deste workspace.';
      } else if (errorCode === 'Workspace.InvitationExpired' || errorCode === 'InvitationExpired') {
        msg = 'Este convite expirou.';
      } else if (errorCode === 'Workspace.InvitationRevoked' || errorCode === 'InvitationRevoked') {
        msg = 'Este convite foi cancelado.';
      } else if (errorCode === 'Workspace.InvitationAlreadyAccepted' || errorCode === 'InvitationAlreadyAccepted') {
        msg = 'Este convite já foi aceito.';
      }

      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  // Missing token guard
  if (!token) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-xl border bg-card p-6 text-center space-y-4 shadow-sm">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-xl font-bold">Link de Convite Inválido</h2>
          <p className="text-sm text-muted-foreground">
            Nenhum token de convite foi fornecido neste link.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Ir para Login
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingInvite) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background p-4 space-y-3">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
        <p className="text-sm text-muted-foreground font-medium">Validando convite...</p>
      </div>
    );
  }

  // Expired or invalid invite
  if (isInviteError || !isValid) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-xl border bg-card p-6 text-center space-y-4 shadow-sm">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold">Convite Expirado ou Inválido</h2>
          <p className="text-sm text-muted-foreground">
            {inviteDetails?.isExpired
              ? 'Este convite expirou. Solicite um novo convite ao administrador do workspace.'
              : 'Este convite já foi aceito ou não é mais válido.'}
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Voltar para o Início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col justify-between bg-gradient-to-b from-background to-muted/20 p-4 sm:p-8">
      {/* Brand Header */}
      <header className="flex items-center justify-center max-w-5xl w-full mx-auto py-2">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-primary/10 text-primary">
            <LogoIcon className="h-6 w-6 text-primary" />
          </div>
          <span className="font-bold text-lg tracking-tight">Tales</span>
        </div>
      </header>

      {/* Main Invitation Card */}
      <main className="flex-1 flex items-center justify-center my-6">
        <div className="w-full max-w-md rounded-2xl border bg-card p-6 sm:p-8 shadow-lg text-center space-y-6">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-8 ring-primary/5">
            <Building2 className="h-7 w-7" />
          </div>

          <div className="space-y-1.5">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Você foi convidado!
            </h1>
            <p className="text-sm text-muted-foreground">
              Você foi convidado para colaborar no workspace <strong>{workspaceName}</strong>.
            </p>
          </div>

          {/* Invitation Details Info */}
          <div className="space-y-2">
            <div className="p-3 rounded-lg bg-muted/40 border border-border/50 flex items-center justify-between text-xs">
              <span className="text-muted-foreground font-medium">Função atribuída:</span>
              <Badge variant="outline" className="font-semibold">{roleName}</Badge>
            </div>

            <div className="p-2.5 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Convite enviado para:</span>
              <span className="font-semibold text-foreground">{targetEmail}</span>
            </div>
          </div>

          {errorMessage && (
            <Alert variant="destructive" className="text-left text-xs py-2.5">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* =====================================================================
              CASE 1: USER IS ALREADY IN THIS WORKSPACE (e.g. Workspace Owner)
             ===================================================================== */}
          {isAuthenticated && isAlreadyMember ? (
            <div className="space-y-4 pt-2">
              <Alert className="border-sky-500/50 bg-sky-500/10 text-sky-900 dark:text-sky-200 text-left text-xs py-3">
                <Check className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                <AlertDescription className="space-y-1">
                  <p>
                    Você já é membro deste workspace com a conta <strong>{user?.email}</strong>.
                  </p>
                  {isEmailMismatch && (
                    <p className="text-muted-foreground text-[11px]">
                      Este convite específico foi enviado para <strong>{targetEmail}</strong>.
                    </p>
                  )}
                </AlertDescription>
              </Alert>

              <div className="space-y-2.5">
                <Button
                  onClick={() => {
                    setWorkspace(inviteDetails.workspaceId);
                    navigate('/conversations');
                  }}
                  className="w-full gap-2 shadow-sm text-sm h-11"
                >
                  <ArrowRight className="h-4 w-4" />
                  <span>Ir para o Painel do Workspace</span>
                </Button>

                {isEmailMismatch && (
                  <Button
                    variant="outline"
                    onClick={handleSwitchAccount}
                    className="w-full gap-2 text-xs h-10 border-border/80"
                  >
                    <LogOut className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Sair e Conectar como {targetEmail}</span>
                  </Button>
                )}
              </div>
            </div>
          ) : isAuthenticated && isEmailMismatch ? (
            /* =====================================================================
               CASE 2: USER IS LOGGED IN WITH DIFFERENT EMAIL (MISMATCH)
               ===================================================================== */
            <div className="space-y-4 pt-2">
              <Alert className="border-amber-500/50 bg-amber-500/10 text-amber-900 dark:text-amber-200 text-left text-xs py-2.5">
                <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription>
                  Você está conectado como <strong>{user?.email}</strong>, mas este convite é exclusivo para <strong>{targetEmail}</strong>.
                </AlertDescription>
              </Alert>

              <div className="space-y-2.5">
                <Button
                  onClick={handleSwitchAccount}
                  className="w-full gap-2 shadow-sm text-sm h-11"
                >
                  <LogOut className="h-4 w-4" />
                  <span>
                    {isUserRegistered
                      ? `Sair e Entrar como ${targetEmail}`
                      : `Sair e Criar Conta para ${targetEmail}`}
                  </span>
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
              </div>
            </div>
          ) : isAuthenticated ? (
            /* =====================================================================
               CASE 3: USER IS LOGGED IN AS TARGET EMAIL (HAPPY PATH)
               ===================================================================== */
            <div className="space-y-4 pt-2">
              <p className="text-xs text-muted-foreground">
                Conectado como <strong>{user?.email}</strong>.
              </p>

              <Button
                onClick={onAcceptInvite}
                disabled={acceptMutation.isPending}
                className="w-full gap-2 shadow-sm text-sm h-11"
              >
                {acceptMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Entrando no workspace...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Aceitar Convite e Entrar</span>
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground pt-1">
                <span>Não é sua conta?</span>
                <button
                  type="button"
                  onClick={handleSwitchAccount}
                  className="font-medium text-primary hover:underline flex items-center gap-1 inline-flex"
                >
                  <LogOut className="h-3 w-3" />
                  Sair e trocar de conta
                </button>
              </div>
            </div>
          ) : (
            /* =====================================================================
               CASE 4: USER IS NOT LOGGED IN (GUEST) - SMART ROUTING VIA isUserRegistered
               ===================================================================== */
            <div className="space-y-4 pt-2">
              <p className="text-xs text-muted-foreground">
                {isUserRegistered
                  ? 'Você já possui uma conta cadastrada com este e-mail. Faça login para aceitar o convite.'
                  : 'Crie sua conta para aceitar o convite e começar a colaborar.'}
              </p>

              <div className="space-y-2.5">
                {isUserRegistered ? (
                  <>
                    <Button
                      onClick={() => navigate(`/login?returnUrl=${inviteReturnUrl}&email=${encodedEmail}`)}
                      className="w-full gap-2 h-11 text-sm shadow-sm"
                    >
                      <LogIn className="h-4 w-4" />
                      <span>Entrar para Aceitar</span>
                      <ArrowRight className="h-4 w-4 ml-auto" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/register?returnUrl=${inviteReturnUrl}&email=${encodedEmail}`)}
                      className="w-full text-xs text-muted-foreground hover:text-foreground"
                    >
                      Criar nova conta com outro e-mail
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => navigate(`/register?returnUrl=${inviteReturnUrl}&email=${encodedEmail}`)}
                      className="w-full gap-2 h-11 text-sm shadow-sm"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span>Criar Conta e Entrar</span>
                      <ArrowRight className="h-4 w-4 ml-auto" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/login?returnUrl=${inviteReturnUrl}&email=${encodedEmail}`)}
                      className="w-full gap-2 h-11 text-sm border-border/80 hover:bg-muted/50"
                    >
                      <LogIn className="h-4 w-4" />
                      <span>Já tenho uma conta</span>
                    </Button>
                  </>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground pt-1">
                Seu e-mail será preenchido automaticamente na próxima etapa.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center text-xs text-muted-foreground py-2">
        Tales &bull; Gestão de Atendimento Inteligente
      </footer>
    </div>
  );
};

export default AcceptInvitePage;
